import React, { useState, useRef, useEffect } from 'react';
import { Download, X } from 'lucide-react';

type ProjectionType = 
  | 'EQUIRECTANGULAR' 
  | 'PILLOW' 
  | 'FLOWER' 
  | 'CUBE' 
  | 'DOMES'
  | 'TENNIS' 
  | 'BASEBALL' 
  | 'BASKETBALL';

interface DownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedProjection: ProjectionType;
  onExportJPG: () => Promise<void>;
  onExportPNG: () => Promise<void>;
  onExportHalfPNG: () => Promise<void>;
  onExportRightHalf: () => Promise<void>;
  onExportWEBP: () => Promise<void>;
  onExportGLB: () => Promise<void>;
  onExportGLTF: () => Promise<void>;
  onExportWEBM: () => Promise<void>;
  onExportSpherePNG: () => Promise<void>;
  onExportPeel: () => Promise<void>;
  onExportBananas: () => Promise<void>;
  onExportPeanuts: () => Promise<void>;
  isExportingVideo: boolean;
  onExportStart: () => void;
  onExportEnd: () => void;
}

const DownloadModal: React.FC<DownloadModalProps> = ({
  isOpen,
  onClose,
  selectedProjection,
  onExportJPG,
  onExportPNG,
  onExportHalfPNG,
  onExportRightHalf,
  onExportWEBP,
  onExportGLB,
  onExportGLTF,
  onExportWEBM,
  onExportSpherePNG,
  onExportPeel,
  onExportBananas,
  onExportPeanuts,
  isExportingVideo,
  onExportStart,
  onExportEnd
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hoveredButtonId, setHoveredButtonId] = useState<string | null>(null);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const progressInterval = useRef<NodeJS.Timeout | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => clearProgress();
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isExporting) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isExporting, onClose]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === modalRef.current) return;
    
    const target = e.target as HTMLElement;
    if (!target.closest('h2') && !target.closest('.progress-bar')) return;

    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });

    e.preventDefault();
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;

    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const clearProgress = () => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
    setProgress(0);
    setIsExporting(false);
    onExportEnd();
  };

  const startProgressAnimation = (duration: number = 10000) => {
    setProgress(0);
    const startTime = Date.now();

    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }

    progressInterval.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const currentProgress = Math.min((elapsed / duration) * 100, 99.9);
      
      if (elapsed >= duration) {
        setProgress(100);
        setTimeout(() => {
          clearProgress();
        }, 500);
      } else {
        setProgress(currentProgress);
      }
    }, 50);
  };

  const handleExport = async (exportFn: () => Promise<void>, buttonId: string) => {
    if (isExporting) return;
    
    setIsExporting(true);
    setHoveredButtonId(buttonId);
    onExportStart();
    
    startProgressAnimation();
    
    try {
      await exportFn();
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      clearProgress();
    }
  };

  const handleWebMExport = () => {
    if (isExporting) return;
    
    setIsExporting(true);
    setHoveredButtonId('webm');
    onExportStart();
    
    startProgressAnimation(15000);
    
    try {
      onExportWEBM();
    } catch (error) {
      console.error('WEBM export failed:', error);
      clearProgress();
    }
  };

  const baseButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '10px 0',
    width: '100%',
    background: '#222222',
    border: '1px solid #333333',
    borderRadius: '6px',
    color: '#00a000',
    fontWeight: 'bold',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    cursor: 'pointer'
  };

  const tooltipStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: 'calc(100% + 8px)',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(24, 24, 24, 0.95)',
    color: '#fff',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '14px',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    opacity: 0,
    transition: 'opacity 0.15s ease',
    zIndex: 1000,
    letterSpacing: '0.5px',
    fontWeight: 'normal',
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
    maxWidth: 'none'
  };

  const ExportButton: React.FC<{
    id: string;
    label: string;
    onClick: () => void;
    isAvailable?: boolean;
    tooltip: string;
    projectionRequired?: string;
    disabled?: boolean;
  }> = ({ 
    id, 
    label, 
    onClick, 
    isAvailable = true, 
    tooltip, 
    projectionRequired = '',
    disabled = false 
  }) => {
    const isHovered = hoveredButtonId === id;
    const showTooltip = activeTooltip === id;
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  
    useEffect(() => {
      if (showTooltip && buttonRef.current && tooltipRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const modalRect = buttonRef.current.closest('.modal-content')?.getBoundingClientRect() ?? { left: 0, right: window.innerWidth };
        const tooltipWidth = tooltipRef.current.offsetWidth;
    
        let positionStyle: React.CSSProperties = {
          position: 'absolute',
          bottom: 'calc(100% + 8px)',
          backgroundColor: 'rgba(24, 24, 24, 0.95)',
          color: '#fff',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '15px',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          opacity: showTooltip ? 1 : 0,
          transition: 'opacity 0.15s ease',
          zIndex: 1000,
          boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
          maxWidth: 'none',
          backdropFilter: 'blur(4px)',
          fontWeight: '400',
          letterSpacing: '0.5px'
        };
    
        if (buttonRect.left < modalRect.left + 50) {
          positionStyle.left = '-10px';
          positionStyle.transform = 'none';
        } else if (buttonRect.right > modalRect.right - 50) {
          positionStyle.right = '-10px';
          positionStyle.transform = 'none';
        } else {
          positionStyle.left = '50%';
          positionStyle.transform = 'translateX(-50%)';
        }
    
        setTooltipStyle(positionStyle);
      }
    }, [showTooltip]);

    return (
      <div 
        style={{ position: 'relative' }}
        onMouseEnter={() => !isExporting && setActiveTooltip(id)}
        onMouseLeave={() => setActiveTooltip(null)}
      >
        <button 
          ref={buttonRef}
          onClick={() => !disabled && isAvailable && onClick()}
          style={{
            ...baseButtonStyle,
            opacity: disabled || !isAvailable ? 0.5 : 1,
            cursor: disabled || !isAvailable ? 'not-allowed' : 'pointer',
            color: isAvailable ? '#00a000' : '#cccccc',
            background: isHovered && isAvailable && !disabled ? '#333333' : '#222222',
            borderColor: isHovered && isAvailable && !disabled ? '#444444' : '#333333',
            transform: isHovered && isAvailable && !disabled ? 'translateY(-1px)' : 'translateY(0)',
            boxShadow: isHovered && isAvailable && !disabled ? '0 2px 8px rgba(0, 160, 0, 0.2)' : 'none'
          }}
          disabled={disabled || !isAvailable}
        >
          <Download 
            size={16} 
            style={{
              transition: 'transform 0.2s ease',
              transform: isHovered && isAvailable && !disabled ? 'scale(1.1)' : 'scale(1)'
            }}
          />
          {label}
          {isExporting && isHovered && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 160, 0, 0.1)',
              borderRadius: '6px'
            }} />
          )}
        </button>
        {showTooltip && (
          <div 
            ref={tooltipRef}
            style={tooltipStyle}
          >
            {!isAvailable && projectionRequired ? 
              `Available in ${projectionRequired} projection` : 
              tooltip}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  const isBasketball = selectedProjection === 'BASKETBALL';
  const isBaseball = selectedProjection === 'BASEBALL';
  const isFlower = selectedProjection === 'FLOWER';

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }} 
      onClick={(e) => !isExporting && e.target === e.currentTarget && onClose()}
    >
      <div 
        ref={modalRef}
        className="modal-content"
        style={{
          backgroundColor: 'rgba(26, 26, 26, 0.26)',
          borderRadius: '12px',
          padding: '24px',
          width: '500px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          color: 'white',
          position: 'relative',
          backdropFilter: 'blur(21px) brightness(1.0)', // brighten the blurred background
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          transform: `translate(${position.x}px, ${position.y}px)`,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
        onMouseDown={handleMouseDown}
        onClick={e => e.stopPropagation()}
      >
        {(isExporting || isExportingVideo) && (
          <div 
            className="progress-bar"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '18px',
              backgroundColor: '#00a000',
              width: `${progress}%`,
              transition: 'width 0.1s linear',
              cursor: 'grab',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: progress === 100 ? '12px' : 0
            }}
          />
        )}

        <h2 style={{
          margin: '0 0 24px 0',
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#fff',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          cursor: 'grab',
          userSelect: 'none',
          paddingRight: '40px'
        }}>
          Download
        </h2>

        <button 
          onClick={() => !isExporting && onClose()}
          style={{
            position: 'absolute',
            right: '16px',
            top: '16px',
            background: 'none',
            border: 'none',
            padding: '8px',
            color: '#FFF',
            cursor: isExporting ? 'not-allowed' : 'pointer',
            opacity: isExporting ? 0.5 : 1
          }}
        >
          <X size={20} strokeWidth={4} />
        </button>

        <div style={{ 
          marginBottom: '24px',
          position: 'relative',
          zIndex: 1
        }}>
          <h3 style={{
            margin: '0 0 12px 0',
            color: '#ccc',
            fontSize: '15px',
            fontWeight: 'normal',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Projection
          </h3>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px',
            width: '100%'
          }}>
            <ExportButton
              id="png"
              label="PNG"
              onClick={() => handleExport(onExportPNG, 'png')}
              tooltip="Export full projection as PNG"
            />
            <ExportButton
              id="jpg"
              label="JPG"
              onClick={() => handleExport(onExportJPG, 'jpg')}
              tooltip="Export as compressed JPEG"
            />
            <ExportButton
              id="webp"
              label="WEBP"
              onClick={() => handleExport(onExportWEBP, 'webp')}
              tooltip="Export as optimized WebP"
            />
          </div>
        </div>

        <div style={{ 
          marginBottom: '24px',
          position: 'relative',
          zIndex: 1
        }}>
          <h3 style={{
            margin: '0 0 12px 0',
            color: '#ccc',
            fontSize: '15px',
            fontWeight: 'normal',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Sphere
          </h3>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px',
            width: '100%'
          }}>
            <ExportButton
              id="webm"
              label="WEBM"
              onClick={handleWebMExport}
              tooltip="Export Sphere Video"
            />
            <ExportButton
              id="sphere-png"
              label="PNG"
              onClick={() => handleExport(onExportSpherePNG, 'sphere-png')}
              tooltip="Export sphere snapshot as PNG"
            />
            <ExportButton
              id="glb"
              label="GLB"
              onClick={() => handleExport(onExportGLB, 'glb')}
              isAvailable={false}
              tooltip="Wishlist"
            />
          </div>
        </div>

        <div style={{ 
          marginBottom: '24px',
          position: 'relative',
          zIndex: 1
        }}>
          <h3 style={{
            margin: '0 0 12px 0',
            color: '#ccc',
            fontSize: '15px',
            fontWeight: 'normal',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Flower
          </h3>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px',
            width: '100%'
          }}>
            <ExportButton
              id="half-left"
              label="½ LEFT"
              onClick={() => handleExport(onExportHalfPNG, 'half-left')}
              isAvailable={isFlower}
              tooltip="Export left half of projection"
              projectionRequired="Flower"
            />
            <ExportButton
              id="half-right"
              label="½ RIGHT"
              onClick={() => handleExport(onExportRightHalf, 'half-right')}
              isAvailable={isFlower}
              tooltip="Export right half of projection"
              projectionRequired="Flower"
            />
          </div>
        </div>

        <div style={{ 
          marginBottom: '24px',
          position: 'relative',
          zIndex: 1
        }}>
          <h3 style={{
            margin: '0 0 12px 0',
            color: '#ccc',
            fontSize: '15px',
            fontWeight: 'normal',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Baseball
          </h3>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px',
            width: '100%'
          }}>
            <ExportButton
              id="peanuts"
              label="PEANUTS"
              onClick={() => handleExport(onExportPeanuts, 'peanuts')}
              isAvailable={isBaseball}
              tooltip="Export Both Baseball Peanuts"
              projectionRequired="Baseball"
            />
          </div>
        </div>

        <div style={{ 
          marginBottom: '24px',
          position: 'relative',
          zIndex: 1
        }}>
          <h3 style={{
            margin: '0 0 12px 0',
            color: '#ccc',
            fontSize: '15px',
            fontWeight: 'normal',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Basketball
          </h3>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px',
            width: '100%'
          }}>
            <ExportButton
              id="peel"
              label="PEEL"
              onClick={() => handleExport(onExportPeel, 'peel')}
              isAvailable={isBasketball}
              tooltip="Export basketball banana sequence"
              projectionRequired="Basketball"
            />
            <ExportButton
              id="bananas"
              label="BANANAS"
              onClick={() => handleExport(onExportBananas, 'bananas')}
              isAvailable={isBasketball}
              tooltip="Export basketball bananas by shape"
              projectionRequired="Basketball"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadModal;